import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { useParams } from 'react-router';

import {
  useAudienceById,
  useAudienceMembership,
  useAudienceMembershipCSVDownload,
} from '@/api/audience';
import AudienceMembershipActionMenu from '@/app/audiences/[audience]/AudienceMembershipActionMenu';
import Error404 from '@/components/errors/Error404';
import { InstantSearch, InstantSearchSortOrder, type QueryState } from '@/components/InstantSearch';
import InstantSearchInput from '@/components/InstantSearchInput';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import { ReturnToLink } from '@/components/ReturnToLink';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import SolAPIError from '@/error/SolAPIError';
import { FileDownloadDialog } from '@/features/FileDownloadModal';
import { ROUTES } from '@/router';

import AudienceMembershipListTable from './AudienceMembershipListTable';

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  sortBy: 'name',
  sortOrder: InstantSearchSortOrder.ASC,
};

const AudienceDetail = () => {
  const [queryState, setQueryState] = useState<QueryState>();
  const { audience: audienceId } = useParams();
  const {
    audience,
    isLoading: isAudienceLoading,
    error: audienceError,
  } = useAudienceById(audienceId);
  const {
    audienceMembership,
    totalResults,
    isLoading: isMembershipLoading,
    error: membershipError,
  } = useAudienceMembership(audienceId, queryState);

  const {
    isLoading: isDownloadingFile,
    beginDownload,
    cancelDownload,
  } = useAudienceMembershipCSVDownload(audienceId, queryState);

  return (
    <Page
      title={t`Audience`}
      pageName={audience?.name}
      exampleAIQuestions={exampleAIQuestions('audience')}
    >
      <div className="flex flex-col gap-6">
        <ReturnToLink route={ROUTES.audiences.path} routeName={<Trans>Audiences</Trans>} />
        <InstantSearch
          prefix="a"
          defaultQueryState={DEFAULT_QUERY_STATE}
          onQueryStateChange={setQueryState}
        >
          {audienceError instanceof SolAPIError && audienceError.is404() ? (
            <Error404 title={<Trans>The audience you're looking for does not exist</Trans>} />
          ) : (
            <>
              <div className="flex items-center justify-end gap-2">
                <PendoPlaceholder />
                <InstantSearchInput className="max-w-80" />
              </div>
              <Panel
                title={
                  audience ? (
                    <Plural
                      value={audience?.metrics.size}
                      one={<Trans>{audience?.name} (# company)</Trans>}
                      other={<Trans>{audience?.name} (# companies)</Trans>}
                    />
                  ) : (
                    <Trans>Audience Details</Trans>
                  )
                }
                isFullHeight
                actions={
                  <AudienceMembershipActionMenu
                    totalResults={totalResults}
                    isLoading={isAudienceLoading || isMembershipLoading}
                    isDownloadingCSV={isDownloadingFile}
                    onDownloadCSV={beginDownload}
                  />
                }
              >
                <AudienceMembershipListTable
                  data={audienceMembership}
                  totalResults={totalResults}
                  isLoading={isAudienceLoading || isMembershipLoading}
                  error={membershipError}
                />
                <FileDownloadDialog open={isDownloadingFile} onAbortDownload={cancelDownload} />
              </Panel>
            </>
          )}
        </InstantSearch>
      </div>
    </Page>
  );
};

export default AudienceDetail;
